import { useEffect, useRef, useState, useCallback } from "react";
import { useAudio } from "../../contexts/Audio";

const AudioVisualizer = ({
    height = 128,
    maxWidth = 300,
    externalOffscreenCanvasRef = null,
    drawingWidthRef = 0,
}) => {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const offscreenCanvasRef = useRef(null);
    const offscreenCtxRef = useRef(null);
    const xRef = useRef(0);
    const yRef = useRef(0);
    const yMirroredRef = useRef(0);
    const offscreenXRef = useRef(0);
    const counterRef = useRef(0);
    const lastMeanFrequencyRef = useRef(0);
    const { isRecording, analyser } = useAudio();

    // State for saved recording
    const [savedRecording, setSavedRecording] = useState(null);
    const [previousRecordingState, setPreviousRecordingState] = useState(false);

    const getResponsiveCanvasHeight = () => {
        // Match recording button height: 128px (mobile) or 144px (desktop)
        return window.innerWidth >= 768 ? 144 : 128;
    };

    const getResponsiveCanvasWidth = useCallback(() => {
        return Math.min(window.outerWidth / 2 - 50, window.innerWidth / 2 - 50, maxWidth);
    }, [maxWidth]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        ctxRef.current = ctx;

        // Create offscreen canvas
        offscreenCanvasRef.current = document.createElement("canvas");
        offscreenCtxRef.current = offscreenCanvasRef.current.getContext("2d");

        if (externalOffscreenCanvasRef) {
            externalOffscreenCanvasRef.current = offscreenCanvasRef.current;
        }

        // Set canvas size
        const width = getResponsiveCanvasWidth();
        const responsiveHeight = getResponsiveCanvasHeight();
        canvas.width = width;
        canvas.height = responsiveHeight;

        // Make offscreen canvas larger to capture entire recording
        const offscreenWidth = 25000; // Enough space for a typical recording
        offscreenCanvasRef.current.width = offscreenWidth;
        offscreenCanvasRef.current.height = responsiveHeight;

        // Set styles for both canvases
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, responsiveHeight);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "rgb(128, 90, 213)";

        offscreenCtxRef.current.fillStyle = "white";
        offscreenCtxRef.current.fillRect(0, 0, offscreenWidth, responsiveHeight);
        offscreenCtxRef.current.lineWidth = 2;
        offscreenCtxRef.current.strokeStyle = "rgb(128, 90, 213)";

        // Reset position references
        xRef.current = width - 1;
        offscreenXRef.current = 0;
        counterRef.current = 0;
        lastMeanFrequencyRef.current = 0;
    }, [height, externalOffscreenCanvasRef, getResponsiveCanvasWidth]);

    // Detect when recording stops and save the visualization
    useEffect(() => {
        // When recording changes from true to false (recording just finished)
        if (previousRecordingState && !isRecording && offscreenCanvasRef.current) {
            // Create trimmed version of the recording
            const offscreenCanvas = offscreenCanvasRef.current;
            const tempCanvas = document.createElement("canvas");
            const tempCtx = tempCanvas.getContext("2d");

            // Use only the part of the canvas that contains the recording
            const actualWidth = Math.min(offscreenXRef.current, offscreenCanvas.width);

            tempCanvas.width = actualWidth;
            tempCanvas.height = offscreenCanvas.height;

            // Copy the used portion to the temp canvas
            //tyle = "white";
            tempCtx.fillRect(0, 0, actualWidth, tempCanvas.height);
            tempCtx.drawImage(
                offscreenCanvas,
                0,
                0,
                actualWidth,
                offscreenCanvas.height,
                0,
                0,
                actualWidth,
                tempCanvas.height
            );

            // Save as data URL
            const recordingDataURL = tempCanvas.toDataURL("image/png");
            setSavedRecording({
                url: recordingDataURL,
                width: actualWidth,
                height: tempCanvas.height,
            });
        }

        setPreviousRecordingState(isRecording);

        // If starting a new recording, clear previous saved recording
        if (isRecording && !previousRecordingState) {
            setSavedRecording(null);
            if (offscreenCtxRef.current && offscreenCanvasRef.current) {
                offscreenCtxRef.current.fillStyle = "white";
                offscreenCtxRef.current.fillRect(
                    0,
                    0,
                    offscreenCanvasRef.current.width,
                    offscreenCanvasRef.current.height
                );
                offscreenXRef.current = 0;
            }
        }
    }, [isRecording, previousRecordingState]);

    useEffect(() => {
        if (!isRecording || !analyser || !ctxRef.current) {
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
            // Clear canvas when not recording
            if (ctxRef.current && canvasRef.current) {
                const ctx = ctxRef.current;
                const canvas = canvasRef.current;
                ctx.fillStyle = "white";
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Reset position for next recording
                xRef.current = canvas.width - 1;
                counterRef.current = 0;
                lastMeanFrequencyRef.current = 0;
            }
            return;
        }

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            if (!isRecording) return;

            let width = getResponsiveCanvasWidth();
            const imageData = ctxRef.current.getImageData(0, 0, width, canvasRef.current.height);
            ctxRef.current.clearRect(0, 0, width, canvasRef.current.height);
            ctxRef.current.putImageData(imageData, -1, 0);

            analyser.getByteFrequencyData(dataArray);
            const meanFrequency = dataArray.reduce((a, b) => a + b) / bufferLength;

            const smoothFrequency = (lastMeanFrequencyRef.current + meanFrequency) / 2;

            if (counterRef.current <= 1) {
                const yHeight = Math.max(
                    ((smoothFrequency / 64) * canvasRef.current.height) / 2,
                    1
                );
                yRef.current = canvasRef.current.height / 2 - yHeight;
                yMirroredRef.current = canvasRef.current.height / 2 + yHeight;

                ctxRef.current.beginPath();
                ctxRef.current.moveTo(xRef.current, yRef.current);
                ctxRef.current.lineTo(xRef.current, yMirroredRef.current);
                ctxRef.current.stroke();

                // draw on offscreen canvas
                if (offscreenCtxRef.current) {
                    offscreenCtxRef.current.beginPath();
                    offscreenCtxRef.current.moveTo(offscreenXRef.current, yRef.current);
                    offscreenCtxRef.current.lineTo(offscreenXRef.current, yMirroredRef.current);
                    offscreenCtxRef.current.stroke();
                    offscreenXRef.current += 4;
                }
            } else if (counterRef.current <= 3) {
                // GAP
            } else {
                counterRef.current = 0;
            }
            counterRef.current++;
            lastMeanFrequencyRef.current = meanFrequency;
            animationFrameIdRef.current = requestAnimationFrame(draw);

            drawingWidthRef.current = offscreenXRef.current;
        };

        draw();

        return () => {
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
        };
    }, [isRecording, analyser, drawingWidthRef, getResponsiveCanvasWidth]);

    // Calculate appropriate height based on aspect ratio and max width
    const getScaledHeight = () => {
        if (!savedRecording) return 0;
        const aspectRatio = savedRecording.width / savedRecording.height;
        const maxDisplayWidth = Math.min(800, window.innerWidth - 40); // Account for margins on small screens
        return maxDisplayWidth / aspectRatio;
    };

    return (
        <div className="">
            {/* Show live visualization during recording */}
            {(!savedRecording || isRecording) && (
                <canvas
                    ref={canvasRef}
                    className="max-w-full rounded-lg h-32 md:h-36 [box-shadow:inset_20px_0_20px_rgba(255,255,255,0.95)]"
                />
            )}

            {/* Show saved recording after recording stops */}
            {savedRecording && !isRecording && (
                <div className="flex justify-center overflow-x-auto">
                    <img
                        src={savedRecording.url}
                        alt="Audio visualization"
                        className="rounded-lg w-full"
                        style={{
                            maxWidth: "800px",
                            height: `${getScaledHeight()}px`,
                        }}
                    />
                </div>
            )}

            {/* Gradient overlay for live visualization */}
            {(!savedRecording || isRecording) && (
                <div className="pointer-events-none absolute inset-y-0 w-20 [background:linear-gradient(to_right,white_0%,white_35%,transparent_100%)] rounded-l-lg" />
            )}
        </div>
    );
};

export default AudioVisualizer;
